import { useMemo } from 'react';
import { Controller, useWatch, type Control, type FieldValues, type Path } from 'react-hook-form';

import { Alert } from '@/ui';

import completionIssue, { type CompletionIssue } from '../schema/completionIssue';
import toFieldDefs from '../schema/toFieldDefs';
import {
  CONTENT_WIDGETS,
  type FieldDef,
  type FormDataValues,
  type FormFieldValue,
  type FormSchema
} from '../types';

import { defaultAutocompleteRegistry, type AutocompleteRegistry } from './autocompleteRegistry';
import widgetRegistry from './widgetRegistry';

interface FormRendererProps<T extends FieldValues = FieldValues> {
  schema: FormSchema;
  control: Control<T>;
  // Prefix for react-hook-form field names, e.g. 'form_data'.
  namespace?: string;
  registry?: AutocompleteRegistry;
  disabled?: boolean;
  // Whether the answers are being held to the form's required and pattern
  // rules (the task is being marked Done). While false, those rules only show
  // as non-blocking hints so a partially answered form can still be saved.
  enforceCompletion?: boolean;
}

const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const FormRenderer = <T extends FieldValues = FieldValues>({
  schema,
  control,
  namespace,
  registry = defaultAutocompleteRegistry,
  disabled,
  enforceCompletion = true
}: FormRendererProps<T>) => {
  const fields = useMemo(() => toFieldDefs(schema), [schema]);

  const fieldName = (field: FieldDef) =>
    (namespace ? `${namespace}.${field.key}` : field.key) as Path<T>;

  const watched: FieldValues = useWatch({ control });
  const answers = ((namespace ? watched?.[namespace] : watched) ?? {}) as FormDataValues;

  // Recomputed every render: answers change on each keystroke anyway, and the
  // check is a handful of comparisons per field.
  const showHints = !disabled && !enforceCompletion;
  const issues = (
    showHints
      ? Object.fromEntries(
          fields
            .filter((field) => !CONTENT_WIDGETS.includes(field.widget))
            .map((field) => [
              field.key,
              completionIssue(field, answers[field.key] as FormFieldValue)
            ])
            .filter(([, issue]) => issue)
        )
      : {}
  ) as Record<string, CompletionIssue | undefined>;

  const outstanding = Object.values(issues);
  const missing = outstanding.filter((issue) => issue?.kind === 'required').length;
  const misformatted = outstanding.length - missing;

  return (
    <div className="flex flex-col gap-4" data-testid="form-renderer">
      {outstanding.length > 0 && (
        <Alert variant="info" className="text-sm">
          {[
            missing ? `${pluralize(missing, 'required answer')} left` : null,
            misformatted ? `${pluralize(misformatted, 'answer')} to fix` : null
          ]
            .filter(Boolean)
            .join(', ')}{' '}
          before this can be marked Done. You can save your progress in the meantime.
        </Alert>
      )}

      {fields.map((field) => {
        const Widget = widgetRegistry[field.widget];

        // Content widgets carry no answer, so they render directly instead
        // of through a Controller-wired form field.
        if (CONTENT_WIDGETS.includes(field.widget)) {
          return (
            <Widget
              key={field.key}
              field={field}
              value={null}
              onChange={() => undefined}
              autocompleteRegistry={registry}
            />
          );
        }

        const issue = issues[field.key];
        return (
          <Controller
            key={field.key}
            name={fieldName(field)}
            control={control}
            render={({ field: rhfField, fieldState }) => (
              <div className="flex flex-col gap-1">
                <Widget
                  field={field}
                  value={rhfField.value}
                  onChange={rhfField.onChange}
                  error={fieldState.error?.message}
                  disabled={disabled}
                  autocompleteRegistry={registry}
                />
                {/* A blank required answer is already flagged by its label's
                    asterisk and the summary; a wrong format is worth saying
                    next to the answer as it's typed. */}
                {!fieldState.error && issue?.kind === 'pattern' && (
                  <p className="text-xs text-yellow-700 dark:text-yellow-300">
                    {issue.message} (needed to mark Done)
                  </p>
                )}
              </div>
            )}
          />
        );
      })}
    </div>
  );
};

export default FormRenderer;
